import { useMemo } from "react";

import { useAppData } from "#app/lib/data";
import { useWorkflowOrder } from "#app/lib/hooks/use-workflow-order";
import { useLastView } from "#app/lib/views/last-view";
import { useModuleRail } from "#app/components/navigation/module-rail/module-rail-context";

/**
 * The Workflows module's landing scope in the user settings bag — ONE key
 * per viewer (`last-view:module:workflows`), beside the per-workflow view
 * memories, so it follows the person across devices.
 */
export const WORKFLOWS_MODULE_SCOPE = { kind: "module", id: "workflows" } as const;

/**
 * The workflow the Workflows module opens on: the tile, the `G W` chord and
 * the "Go to Workflows" command all land here.
 *
 * The last workflow the viewer opened wins, when it is still theirs to
 * reach. `useAppData().workflows` is the API's visibility-scoped set, so a
 * workflow whose access was revoked, or that was deleted, is simply absent
 * and the memory falls through; an archived one falls through too unless
 * the sidebar shows archived workflows. With no usable memory the module
 * lands on the first row the sidebar's Workflows list renders (the viewer's
 * order, the same scope toggles), and with no visible workflows at all on
 * the roster page (`undefined`).
 */
export function useWorkflowsLanding(): string | undefined {
  const { workflows } = useAppData();
  const { sortedWorkflows } = useWorkflowOrder(workflows);
  const { showAllWorkflows, showArchivedWorkflows } = useModuleRail();
  const { lastKey } = useLastView(WORKFLOWS_MODULE_SCOPE);

  return useMemo(() => {
    const reachable = sortedWorkflows.filter(
      (wf) => showArchivedWorkflows || !wf.archived,
    );
    const last = reachable.find((wf) => wf.workflowId === lastKey);
    if (last) return last.workflowId;
    const listed = reachable.filter(
      (wf) => showAllWorkflows || wf.viewerHasExplicitAccess,
    );
    return (listed[0] ?? reachable[0])?.workflowId;
  }, [sortedWorkflows, showAllWorkflows, showArchivedWorkflows, lastKey]);
}
